import { useEffect, useRef, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const Documentation = () => {
  const containerRef = useRef(null);
  const [activeSection, setActiveSection] = useState('getting-started');

  const sections = useMemo(() => [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
    },
    {
      id: 'installation',
      title: 'Installation',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      ),
    },
    {
      id: 'configuration',
      title: 'Configuration',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
    },
    {
      id: 'features',
      title: 'Features',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
      ),
    },
    {
      id: 'shortcuts',
      title: 'Keyboard Shortcuts',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707" />
        </svg>
      ),
    },
    {
      id: 'notifications',
      title: 'Notifications',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      ),
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      id: 'faq',
      title: 'FAQ',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
  ], []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Animate content sections on scroll
      gsap.utils.toArray('.doc-section').forEach((section) => {
        gsap.fromTo(section,
          { y: 40, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.8,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: section,
              start: 'top 85%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      });
    }, containerRef);

    return () => ctx.revert();
  }, []);

  // Update active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = sections.map(s => document.getElementById(s.id));
      const scrollPos = window.scrollY + 150;

      for (let i = sectionElements.length - 1; i >= 0; i--) {
        const section = sectionElements[i];
        if (section && section.offsetTop <= scrollPos) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 100,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div ref={containerRef} className="min-h-screen bg-dark-950">
      {/* Background */}
      <div className="fixed inset-0 z-0">
        <div className="absolute top-0 left-1/4 w-[300px] h-[300px] sm:w-[400px] sm:h-[400px] md:w-[600px] md:h-[600px] bg-accent-500/10 rounded-full blur-[100px] md:blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[250px] h-[250px] sm:w-[350px] sm:h-[350px] md:w-[500px] md:h-[500px] bg-blue-500/10 rounded-full blur-[80px] md:blur-[130px]" />
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 py-3 sm:py-4 bg-dark-950/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 group">
            <div className="relative w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-accent-500 to-accent-600 rounded-lg sm:rounded-xl flex items-center justify-center overflow-hidden">
              <svg className="relative z-10 w-5 h-5 sm:w-6 sm:h-6 text-dark-950" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 2.824a9.176 9.176 0 110 18.352 9.176 9.176 0 010-18.352zM7.412 12a4.588 4.588 0 119.176 0 4.588 4.588 0 01-9.176 0z"/>
              </svg>
            </div>
            <span className="text-lg sm:text-xl font-bold text-white group-hover:text-accent-400 transition-colors">
              Commit Watch
            </span>
          </Link>
          
          <div className="flex items-center gap-2 sm:gap-4">
            <span className="hidden sm:block text-dark-400 text-sm">Documentation</span>
            <Link
              to="/"
              className="px-3 sm:px-4 py-2 text-sm sm:text-base text-dark-300 hover:text-white transition-colors"
            >
              <span className="hidden sm:inline">← Back to Home</span>
              <span className="sm:hidden">← Home</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="relative z-10 pt-20 sm:pt-24 pb-12 sm:pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {/* Hero Section */}
          <div className="text-center mb-8 sm:mb-12 md:mb-16 pt-4 sm:pt-8">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6">
              <span className="gradient-text">Documentation</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-dark-300 max-w-2xl mx-auto px-2">
              Everything you need to know about installing, configuring, and using CommitWatch effectively.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-6 sm:gap-8 lg:gap-10">
            {/* Sidebar Navigation */}
            <aside className="lg:w-64 flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <nav className="glass rounded-xl sm:rounded-2xl border border-white/10 p-3 sm:p-4">
                  <h3 className="text-xs font-bold text-dark-400 uppercase tracking-wider mb-3 sm:mb-4 px-2 sm:px-3">
                    On this page
                  </h3>
                  {/* Horizontal scroll on mobile, vertical on desktop */}
                  <div className="lg:block overflow-x-auto lg:overflow-x-visible pb-2 lg:pb-0 -mx-1 px-1">
                    <ul className="flex lg:flex-col gap-1 lg:space-y-1 min-w-max lg:min-w-0">
                      {sections.map((section) => (
                        <li key={section.id}>
                          <button
                            onClick={() => scrollToSection(section.id)}
                            className={`w-full flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2 rounded-lg text-left transition-all duration-300 whitespace-nowrap lg:whitespace-normal ${
                              activeSection === section.id
                                ? 'bg-accent-500/20 text-accent-400'
                                : 'text-dark-400 hover:text-white hover:bg-white/5'
                            }`}
                          >
                            {section.icon}
                            <span className="text-xs sm:text-sm font-medium">{section.title}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </nav>
              </div>
            </aside>

            {/* Content */}
            <main className="flex-1 min-w-0">
              {/* Getting Started */}
              <section id="getting-started" className="doc-section mb-8 sm:mb-12 md:mb-16">
                <div className="glass rounded-xl sm:rounded-2xl border border-white/10 p-4 sm:p-6 md:p-8">
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                    <span className="w-8 h-8 sm:w-10 sm:h-10 bg-accent-500/20 rounded-lg flex items-center justify-center text-accent-400 flex-shrink-0">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </span>
                    Getting Started
                  </h2>
                  <div className="prose prose-invert max-w-none">
                    <p className="text-dark-300 text-base sm:text-lg leading-relaxed mb-4 sm:mb-6">
                      CommitWatch is a browser extension that delivers smart commit notifications for both GitHub and GitLab. 
                      It helps you stay updated on repositories you care about—on GitHub or GitLab—without drowning in noise.
                    </p>
                    <div className="bg-dark-800/50 rounded-lg sm:rounded-xl p-4 sm:p-6 border border-white/5">
                      <h4 className="text-white font-semibold mb-3 text-sm sm:text-base">Quick Start Steps:</h4>
                      <ol className="space-y-2 sm:space-y-3 text-dark-300 text-sm sm:text-base">
                        <li className="flex items-start gap-2 sm:gap-3">
                          <span className="w-5 h-5 sm:w-6 sm:h-6 bg-accent-500 text-dark-950 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0 mt-0.5">1</span>
                          <span>Install the extension from the GitHub releases page</span>
                        </li>
                        <li className="flex items-start gap-2 sm:gap-3">
                          <span className="w-5 h-5 sm:w-6 sm:h-6 bg-accent-500 text-dark-950 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0 mt-0.5">2</span>
                          <span>Sign in with your GitHub or GitLab account using secure OAuth or personal access token</span>
                        </li>
                        <li className="flex items-start gap-2 sm:gap-3">
                          <span className="w-5 h-5 sm:w-6 sm:h-6 bg-accent-500 text-dark-950 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0 mt-0.5">3</span>
                          <span>Configure check interval and notification preferences</span>
                        </li>
                        <li className="flex items-start gap-2 sm:gap-3">
                          <span className="w-5 h-5 sm:w-6 sm:h-6 bg-accent-500 text-dark-950 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0 mt-0.5">4</span>
                          <span>Start receiving prioritized commit notifications!</span>
                        </li>
                      </ol>
                    </div>
                  </div>
                </div>
              </section>

              {/* Installation */}
              <section id="installation" className="doc-section mb-8 sm:mb-12 md:mb-16">
                <div className="glass rounded-xl sm:rounded-2xl border border-white/10 p-4 sm:p-6 md:p-8">
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                    <span className="w-8 h-8 sm:w-10 sm:h-10 bg-accent-500/20 rounded-lg flex items-center justify-center text-accent-400 flex-shrink-0">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                    </span>
                    Installation
                  </h2>
                  <div className="space-y-4 sm:space-y-6">
                    <div className="bg-dark-800/50 rounded-lg sm:rounded-xl p-4 sm:p-6 border border-white/5">
                      <h4 className="text-white font-semibold mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-accent-400" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/>
                        </svg>
                        Chrome / Edge / Brave
                      </h4>
                      <ol className="space-y-2 sm:space-y-3 text-dark-300 text-sm sm:text-base">
                        <li>1. Download the latest release from <a href="https://github.com/Mohammed-3tef/CommitWatch/releases/latest" target="_blank" rel="noopener noreferrer" className="text-accent-400 hover:underline">GitHub Releases</a></li>
                        <li>2. Extract the ZIP file to a folder</li>
                        <li>3. Open <code className="bg-dark-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-xs sm:text-sm">chrome://extensions</code> in your browser</li>
                        <li>4. Enable "Developer mode" in the top right</li>
                        <li>5. Click "Load unpacked" and select the extracted folder</li>
                        <li>6. You can use CommitWatch with GitHub or GitLab accounts.</li>
                      </ol>
                    </div>
                    
                    <div className="bg-dark-800/50 rounded-lg sm:rounded-xl p-4 sm:p-6 border border-white/5">
                      <h4 className="text-white font-semibold mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-orange-400" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                        </svg>
                        Firefox
                      </h4>
                      <ol className="space-y-2 sm:space-y-3 text-dark-300 text-sm sm:text-base">
                        <li>1. Download the Firefox-specific release (XPI file)</li>
                        <li>2. Open <code className="bg-dark-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-xs sm:text-sm">about:addons</code></li>
                        <li>3. Click the gear icon and select "Install Add-on From File"</li>
                        <li>4. Select the downloaded XPI file</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </section>

              {/* Configuration */}
              <section id="configuration" className="doc-section mb-8 sm:mb-12 md:mb-16">
                <div className="glass rounded-xl sm:rounded-2xl border border-white/10 p-4 sm:p-6 md:p-8">
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                    <span className="w-8 h-8 sm:w-10 sm:h-10 bg-accent-500/20 rounded-lg flex items-center justify-center text-accent-400 flex-shrink-0">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </span>
                    Configuration
                  </h2>
                  <div className="space-y-4 sm:space-y-6">
                    <p className="text-dark-300 text-base sm:text-lg leading-relaxed">
                      CommitWatch can be configured to match your workflow. Access settings by clicking the extension icon and selecting "Settings".
                    </p>
                    
                    <div className="grid gap-3 sm:gap-4">
                      <div className="bg-dark-800/50 rounded-lg sm:rounded-xl p-4 sm:p-5 border border-white/5">
                        <h4 className="text-white font-semibold mb-1 sm:mb-2 text-sm sm:text-base">Check Interval</h4>
                        <p className="text-dark-400 text-xs sm:text-sm">Set how often to poll for new commits (1-60 minutes) to balance freshness and API usage.</p>
                      </div>
                      <div className="bg-dark-800/50 rounded-lg sm:rounded-xl p-4 sm:p-5 border border-white/5">
                        <h4 className="text-white font-semibold mb-1 sm:mb-2 text-sm sm:text-base">Notification Types</h4>
                        <p className="text-dark-400 text-xs sm:text-sm">Toggle commit, GitHub, and release notifications independently based on your needs.</p>
                      </div>
                      <div className="bg-dark-800/50 rounded-lg sm:rounded-xl p-4 sm:p-5 border border-white/5">
                        <h4 className="text-white font-semibold mb-1 sm:mb-2 text-sm sm:text-base">Sound Alerts</h4>
                        <p className="text-dark-400 text-xs sm:text-sm">Enable optional audio notifications for new commits to never miss an update.</p>
                      </div>
                      <div className="bg-dark-800/50 rounded-lg sm:rounded-xl p-4 sm:p-5 border border-white/5">
                        <h4 className="text-white font-semibold mb-1 sm:mb-2 text-sm sm:text-base">Theme Selection</h4>
                        <p className="text-dark-400 text-xs sm:text-sm">Choose between light, dark, or system-synced themes for comfortable viewing.</p>
                      </div>
                      <div className="bg-dark-800/50 rounded-lg sm:rounded-xl p-4 sm:p-5 border border-white/5">
                        <h4 className="text-white font-semibold mb-1 sm:mb-2 text-sm sm:text-base">Filter Options</h4>
                        <p className="text-dark-400 text-xs sm:text-sm">Show only your own commits or view all team activity based on preference.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Features */}
              <section id="features" className="doc-section mb-8 sm:mb-12 md:mb-16">
                <div className="glass rounded-xl sm:rounded-2xl border border-white/10 p-4 sm:p-6 md:p-8">
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                    <span className="w-8 h-8 sm:w-10 sm:h-10 bg-accent-500/20 rounded-lg flex items-center justify-center text-accent-400 flex-shrink-0">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                    </span>
                    Features
                  </h2>
                  <div className="space-y-6 sm:space-y-8">
                    {/* Real-Time Notifications */}
                    <div className="bg-dark-800/50 rounded-lg sm:rounded-xl p-4 sm:p-6 border border-white/5">
                      <h4 className="text-white font-semibold mb-3 sm:mb-4 flex items-center gap-2 text-base sm:text-lg">
                        <span className="text-xl"></span> Real-Time Notifications
                      </h4>
                      <ul className="space-y-2 text-dark-300 text-sm sm:text-base">
                        <li className="flex items-start gap-2"><span className="text-accent-400">•</span><span><strong className="text-white">Instant Commit Alerts</strong> - Get notified the moment new commits are pushed</span></li>
                        <li className="flex items-start gap-2"><span className="text-accent-400">•</span><span><strong className="text-white">GitHub & GitLab Notifications Sync</strong> - Stay updated on PR reviews, merge requests, mentions, CI failures</span></li>
                        <li className="flex items-start gap-2"><span className="text-accent-400">•</span><span><strong className="text-white">Release Tag Monitoring</strong> - Get notified about new version releases and tags</span></li>
                        <li className="flex items-start gap-2"><span className="text-accent-400">•</span><span><strong className="text-white">Smart Polling</strong> - Configurable check intervals (1-60 minutes)</span></li>
                        <li className="flex items-start gap-2"><span className="text-accent-400">•</span><span><strong className="text-white">Desktop Notifications</strong> - Native Chrome notifications that work even when minimized</span></li>
                      </ul>
                    </div>

                    {/* Priority Classification */}
                    <div className="bg-dark-800/50 rounded-lg sm:rounded-xl p-4 sm:p-6 border border-white/5">
                      <h4 className="text-white font-semibold mb-3 sm:mb-4 flex items-center gap-2 text-base sm:text-lg">
                        <span className="text-xl"></span> Priority Classification
                      </h4>
                      <ul className="space-y-2 text-dark-300 text-sm sm:text-base">
                        <li className="flex items-start gap-2"><span className="text-accent-400">•</span><span><strong className="text-white">Intelligent Prioritization</strong> - Commits automatically classified by importance</span></li>
                        <li className="flex items-start gap-2"><span className="w-3 h-3 bg-red-500 rounded-full mt-1.5 flex-shrink-0"></span><span><strong className="text-white">High Priority</strong> - Critical fixes, security patches, breaking changes, hotfixes</span></li>
                        <li className="flex items-start gap-2"><span className="w-3 h-3 bg-yellow-500 rounded-full mt-1.5 flex-shrink-0"></span><span><strong className="text-white">Medium Priority</strong> - Regular team commits, feature branches</span></li>
                        <li className="flex items-start gap-2"><span className="w-3 h-3 bg-green-500 rounded-full mt-1.5 flex-shrink-0"></span><span><strong className="text-white">Low Priority</strong> - Merge commits, documentation, formatting, style changes</span></li>
                        <li className="flex items-start gap-2"><span className="text-accent-400">•</span><span><strong className="text-white">Owner Detection</strong> - Commits to main/master by repository owners flagged as high priority</span></li>
                      </ul>
                    </div>

                    {/* Modern Interface */}
                    <div className="bg-dark-800/50 rounded-lg sm:rounded-xl p-4 sm:p-6 border border-white/5">
                      <h4 className="text-white font-semibold mb-3 sm:mb-4 flex items-center gap-2 text-base sm:text-lg">
                        <span className="text-xl"></span> Modern Interface
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {[
                          { title: 'Clean Popup Design', desc: 'Beautiful, distraction-free notification center' },
                          { title: 'Dark & Light Themes', desc: 'Choose your preferred theme or sync with system' },
                          { title: 'Responsive Layout', desc: 'Works perfectly on any screen size' },
                          { title: 'Real-Time Badge', desc: 'See unread notification count on the extension icon' },
                          { title: 'Grouped Notifications', desc: 'Commits organized by repository for easy scanning' },
                        ].map((item, i) => (
                          <div key={i} className="flex items-start gap-2 text-dark-300 text-sm">
                            <span className="text-accent-400">•</span>
                            <span><strong className="text-white">{item.title}</strong> - {item.desc}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Security & Privacy */}
                    <div className="bg-dark-800/50 rounded-lg sm:rounded-xl p-4 sm:p-6 border border-white/5">
                      <h4 className="text-white font-semibold mb-3 sm:mb-4 flex items-center gap-2 text-base sm:text-lg">
                        <span className="text-xl"></span> Security & Privacy
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {[
                          { title: 'Secure OAuth', desc: 'Industry-standard GitHub OAuth authentication' },
                          { title: 'Minimal Permissions', desc: 'Only requests necessary GitHub scopes' },
                          { title: 'Local Storage', desc: 'All data stored locally on your device' },
                          { title: 'No Tracking', desc: 'Zero analytics or third-party data collection' },
                          { title: 'Open Source', desc: 'Fully transparent, auditable codebase' },
                        ].map((item, i) => (
                          <div key={i} className="flex items-start gap-2 text-dark-300 text-sm">
                            <span className="text-accent-400">•</span>
                            <span><strong className="text-white">{item.title}</strong> - {item.desc}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Performance */}
                    <div className="bg-dark-800/50 rounded-lg sm:rounded-xl p-4 sm:p-6 border border-white/5">
                      <h4 className="text-white font-semibold mb-3 sm:mb-4 flex items-center gap-2 text-base sm:text-lg">
                        <span className="text-xl"></span> Performance
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {[
                          { title: 'Lightweight', desc: 'Minimal memory and CPU footprint' },
                          { title: 'Efficient API Usage', desc: 'Smart caching and rate limit handling' },
                          { title: 'Background Processing', desc: 'Silent updates without interrupting workflow' },
                          { title: 'Instant Loading', desc: 'Popup opens immediately with cached data' },
                        ].map((item, i) => (
                          <div key={i} className="flex items-start gap-2 text-dark-300 text-sm">
                            <span className="text-accent-400">•</span>
                            <span><strong className="text-white">{item.title}</strong> - {item.desc}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Keyboard Shortcuts */}
              <section id="shortcuts" className="doc-section mb-8 sm:mb-12 md:mb-16">
                <div className="glass rounded-xl sm:rounded-2xl border border-white/10 p-4 sm:p-6 md:p-8">
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                    <span className="w-8 h-8 sm:w-10 sm:h-10 bg-accent-500/20 rounded-lg flex items-center justify-center text-accent-400 flex-shrink-0">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707" />
                      </svg>
                    </span>
                    Keyboard Shortcuts
                  </h2>
                  <div className="space-y-4 sm:space-y-6">
                    <div className="bg-dark-800/50 rounded-lg sm:rounded-xl p-4 sm:p-6 border border-white/5">
                      <h4 className="text-white font-semibold mb-3 sm:mb-4 text-sm sm:text-base">Global Shortcuts (Work Anywhere in Chrome)</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-dark-400 border-b border-white/10">
                              <th className="pb-2 pr-4">Shortcut</th>
                              <th className="pb-2">Action</th>
                            </tr>
                          </thead>
                          <tbody className="text-dark-300">
                            <tr className="border-b border-white/5">
                              <td className="py-2 pr-4"><code className="bg-dark-700 px-2 py-1 rounded text-accent-400">Alt + Shift + C</code></td>
                              <td className="py-2">Open Commit Watch popup</td>
                            </tr>
                            <tr className="border-b border-white/5">
                              <td className="py-2 pr-4"><code className="bg-dark-700 px-2 py-1 rounded text-accent-400">Alt + Shift + R</code></td>
                              <td className="py-2">Check for new commits now</td>
                            </tr>
                            <tr>
                              <td className="py-2 pr-4"><code className="bg-dark-700 px-2 py-1 rounded text-accent-400">Alt + Shift + M</code></td>
                              <td className="py-2">Mark all as read</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                    
                    <div className="bg-dark-800/50 rounded-lg sm:rounded-xl p-4 sm:p-6 border border-white/5">
                      <h4 className="text-white font-semibold mb-3 sm:mb-4 text-sm sm:text-base">Popup Shortcuts (When Popup is Open)</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-dark-400 border-b border-white/10">
                              <th className="pb-2 pr-4">Shortcut</th>
                              <th className="pb-2">Action</th>
                            </tr>
                          </thead>
                          <tbody className="text-dark-300">
                            <tr className="border-b border-white/5">
                              <td className="py-2 pr-4"><code className="bg-dark-700 px-2 py-1 rounded text-accent-400">R</code></td>
                              <td className="py-2">Refresh / Check now</td>
                            </tr>
                            <tr className="border-b border-white/5">
                              <td className="py-2 pr-4"><code className="bg-dark-700 px-2 py-1 rounded text-accent-400">M</code></td>
                              <td className="py-2">Mark all as read</td>
                            </tr>
                            <tr className="border-b border-white/5">
                              <td className="py-2 pr-4"><code className="bg-dark-700 px-2 py-1 rounded text-accent-400">S</code></td>
                              <td className="py-2">Open settings</td>
                            </tr>
                            <tr className="border-b border-white/5">
                              <td className="py-2 pr-4"><code className="bg-dark-700 px-2 py-1 rounded text-accent-400">T</code></td>
                              <td className="py-2">Toggle theme</td>
                            </tr>
                            <tr className="border-b border-white/5">
                              <td className="py-2 pr-4"><code className="bg-dark-700 px-2 py-1 rounded text-accent-400">L</code></td>
                              <td className="py-2">Logout</td>
                            </tr>
                            <tr>
                              <td className="py-2 pr-4"><code className="bg-dark-700 px-2 py-1 rounded text-accent-400">Esc</code></td>
                              <td className="py-2">Close popup</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                    
                    <p className="text-dark-400 text-sm">
                      <strong className="text-white">Tip:</strong> You can customize global shortcuts via Chrome's extension settings at <code className="bg-dark-700 px-2 py-1 rounded text-xs">chrome://extensions/shortcuts</code>
                    </p>
                  </div>
                </div>
              </section>

              {/* Notifications */}
              <section id="notifications" className="doc-section mb-8 sm:mb-12 md:mb-16">
                <div className="glass rounded-xl sm:rounded-2xl border border-white/10 p-4 sm:p-6 md:p-8">
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                    <span className="w-8 h-8 sm:w-10 sm:h-10 bg-accent-500/20 rounded-lg flex items-center justify-center text-accent-400 flex-shrink-0">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                    </span>
                    Notifications
                  </h2>
                  <div className="space-y-4 sm:space-y-6">
                    <p className="text-dark-300 text-base sm:text-lg leading-relaxed">
                      CommitWatch uses browser notifications to keep you informed. Here's how to make the most of them:
                    </p>
                    
                    <div className="bg-dark-800/50 rounded-lg sm:rounded-xl p-4 sm:p-6 border border-white/5">
                      <h4 className="text-white font-semibold mb-3 sm:mb-4 text-sm sm:text-base">Notification Types</h4>
                      <ul className="space-y-2 sm:space-y-3 text-dark-300 text-sm sm:text-base">
                        <li className="flex items-start gap-2 sm:gap-3">
                          <span className="w-2 h-2 bg-green-400 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></span>
                          <span><strong className="text-white">Commit Notifications:</strong> Instant alerts when new commits are pushed to tracked repositories</span>
                        </li>
                        <li className="flex items-start gap-2 sm:gap-3">
                          <span className="w-2 h-2 bg-blue-400 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></span>
                          <span><strong className="text-white">GitHub Notifications:</strong> PR reviews, mentions, CI failures, and other GitHub activity</span>
                        </li>
                        <li className="flex items-start gap-2 sm:gap-3">
                          <span className="w-2 h-2 bg-purple-400 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></span>
                          <span><strong className="text-white">Release Tags:</strong> Get notified about new version releases and tags</span>
                        </li>
                      </ul>
                    </div>
                    
                    <div className="bg-dark-800/50 rounded-lg sm:rounded-xl p-4 sm:p-6 border border-white/5">
                      <h4 className="text-white font-semibold mb-3 sm:mb-4 text-sm sm:text-base">Priority Levels</h4>
                      <ul className="space-y-2 sm:space-y-3 text-dark-300 text-sm sm:text-base">
                        <li className="flex items-start gap-2 sm:gap-3">
                          <span className="w-2 h-2 bg-red-500 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></span>
                          <span><strong className="text-white">High Priority:</strong> Critical fixes, security patches, breaking changes, hotfixes</span>
                        </li>
                        <li className="flex items-start gap-2 sm:gap-3">
                          <span className="w-2 h-2 bg-yellow-500 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></span>
                          <span><strong className="text-white">Medium Priority:</strong> Regular team commits, feature branches</span>
                        </li>
                        <li className="flex items-start gap-2 sm:gap-3">
                          <span className="w-2 h-2 bg-green-500 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></span>
                          <span><strong className="text-white">Low Priority:</strong> Merge commits, documentation, formatting, style changes</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Troubleshooting */}
              <section id="troubleshooting" className="doc-section mb-8 sm:mb-12 md:mb-16">
                <div className="glass rounded-xl sm:rounded-2xl border border-white/10 p-4 sm:p-6 md:p-8">
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                    <span className="w-8 h-8 sm:w-10 sm:h-10 bg-accent-500/20 rounded-lg flex items-center justify-center text-accent-400 flex-shrink-0">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </span>
                    Troubleshooting
                  </h2>
                  <div className="space-y-3 sm:space-y-4">
                    {[
                      {
                        q: 'Notifications not appearing?',
                        a: 'Make sure browser notifications are enabled for the extension. Check your system notification settings as well.'
                      },
                      {
                        q: 'Extension not loading?',
                        a: 'Try disabling and re-enabling the extension. If that doesn\'t work, remove and reinstall it.'
                      },
                      {
                        q: 'Rate limiting issues?',
                        a: 'GitHub and GitLab have API rate limits. Consider adding a personal access token in settings for higher limits.'
                      },
                      {
                        q: 'Commits not syncing?',
                        a: 'Check your internet connection and ensure the repository URL is correct and accessible.'
                      },
                    ].map((item, i) => (
                      <div key={i} className="bg-dark-800/50 rounded-lg sm:rounded-xl p-4 sm:p-5 border border-white/5">
                        <h4 className="text-white font-semibold mb-1 sm:mb-2 text-sm sm:text-base">{item.q}</h4>
                        <p className="text-dark-400 text-xs sm:text-sm">{item.a}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* FAQ */}
              <section id="faq" className="doc-section mb-8 sm:mb-12 md:mb-16">
                <div className="glass rounded-xl sm:rounded-2xl border border-white/10 p-4 sm:p-6 md:p-8">
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                    <span className="w-8 h-8 sm:w-10 sm:h-10 bg-accent-500/20 rounded-lg flex items-center justify-center text-accent-400 flex-shrink-0">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </span>
                    Frequently Asked Questions
                  </h2>
                  <div className="space-y-3 sm:space-y-4">
                    {[
                      {
                        q: 'Is CommitWatch free?',
                        a: 'Yes! CommitWatch is completely free and open source under the MPL license.'
                      },
                      {
                        q: 'Does it work with private repositories?',
                        a: 'Yes! When you sign in with GitHub OAuth, you can access both public and private repositories based on the permissions you grant.'
                      },
                      {
                        q: 'How does priority classification work?',
                        a: 'Commits are automatically analyzed and classified as High (critical fixes, security), Medium (regular commits), or Low (docs, formatting) based on commit messages and context.'
                      },
                      {
                        q: 'Is my data secure?',
                        a: 'Absolutely. All data is stored locally in your browser using secure OAuth. We have zero analytics or third-party data collection.'
                      },
                      {
                        q: 'How do I customize keyboard shortcuts?',
                        a: 'Global shortcuts can be customized via Chrome\'s extension settings at chrome://extensions/shortcuts.'
                      },
                      {
                        q: 'Can I contribute to the project?',
                        a: 'Yes! The codebase is fully open source and auditable. Check out our GitHub repository for contribution guidelines.'
                      },
                    ].map((item, i) => (
                      <div key={i} className="bg-dark-800/50 rounded-lg sm:rounded-xl p-4 sm:p-5 border border-white/5">
                        <h4 className="text-white font-semibold mb-1 sm:mb-2 text-sm sm:text-base">{item.q}</h4>
                        <p className="text-dark-400 text-xs sm:text-sm">{item.a}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* Help CTA */}
              <section className="doc-section">
                <div className="glass rounded-xl sm:rounded-2xl border border-white/10 p-4 sm:p-6 md:p-8 text-center">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-2 sm:mb-4">Still need help?</h3>
                  <p className="text-dark-300 text-sm sm:text-base mb-4 sm:mb-6">
                    Can't find what you're looking for? Open an issue on GitHub or check out our discussions.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
                    <a
                      href="https://github.com/Mohammed-3tef/CommitWatch/issues"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full sm:w-auto px-5 sm:px-6 py-2.5 sm:py-3 bg-accent-500 text-dark-950 font-bold text-sm sm:text-base rounded-lg sm:rounded-xl hover:scale-105 transition-transform"
                    >
                      Report an Issue
                    </a>
                    <a
                      href="https://github.com/Mohammed-3tef/CommitWatch"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full sm:w-auto px-5 sm:px-6 py-2.5 sm:py-3 glass text-white font-semibold text-sm sm:text-base rounded-lg sm:rounded-xl border border-white/20 hover:border-accent-500/50 hover:scale-105 transition-all"
                    >
                      View on GitHub
                    </a>
                  </div>
                </div>
              </section>
            </main>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 py-6 sm:py-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-dark-500 text-xs sm:text-sm">
            © {new Date().getFullYear()} Commit Watch. Open source under MPL License.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Documentation;
